import { GiftsListResponse, GiftActivateResponse, GiftConfirmResponse } from '@lunit/shared-types';
import { api } from './client';
import { telegramIdField, withTelegramId } from './session';

// ============================================================================
// Gift Templates (catalog)
// ============================================================================

export interface GiftTemplatePublic {
  id: string;
  type: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  value: Record<string, unknown>;
  costInPoints: number | null;
  expiresInDays: number;
  isVisible: boolean;
  sortOrder: number;
}

/**
 * Get visible gift templates for venue (public catalog)
 */
export async function getGiftTemplates(): Promise<GiftTemplatePublic[]> {
  const result = await api.get<{ templates: GiftTemplatePublic[] }>('/gift-templates');
  return result.templates ?? [];
}

/**
 * Get user's gifts (PENDING + recent ACTIVATED). Клиент — из сессии; telegramId — необязательная сверка.
 */
export async function getMyGifts(telegramId?: number | null): Promise<GiftsListResponse> {
  return api.get<GiftsListResponse>(withTelegramId('/gifts', telegramId));
}

/**
 * Activate a gift
 */
export async function activateGift(giftId: string, telegramId?: number | null): Promise<GiftActivateResponse> {
  return api.post<GiftActivateResponse>(`/gifts/${giftId}/activate`, telegramIdField(telegramId));
}

/**
 * Confirm a PRODUCT gift by 6-digit code (cashier/user button)
 */
export async function confirmGift(code: string): Promise<GiftConfirmResponse> {
  return api.post<GiftConfirmResponse>('/gifts/confirm', { code });
}

// ============================================================================
// Gift Links (P2P)
// ============================================================================

export interface GiftLinkData {
  id: string;
  claimCode: string;
  giftType: string;
  title: string;
  expiresAt: string;
  deepLink: string;
}

export interface ClaimGiftLinkResult {
  giftLink: { id: string; claimCode: string; status: string; giftId: string | null };
  gift: { id: string; type: string; title: string; status: string };
}

/**
 * Create a gift link (P2P sharing).
 * Backend charges the template's costInPoints from the sender and takes type/title/value/cost
 * authoritatively from the template (client only picks the template + an optional message).
 */
export async function createGiftLink(params: {
  telegramId?: number | null;
  templateId: string;
  message?: string;
}): Promise<GiftLinkData> {
  // silent: ошибку показываем своим баннером на странице, а не глобальным тостом (без дубля).
  return api.post<GiftLinkData>('/gift-links', {
    templateId: params.templateId,
    message: params.message,
    ...telegramIdField(params.telegramId),
  }, { silent: true });
}

/**
 * Claim a gift link by code
 */
export async function claimGiftLink(
  claimCode: string,
  telegramId?: number | null
): Promise<ClaimGiftLinkResult> {
  return api.post<ClaimGiftLinkResult>('/gift-links/claim', {
    claimCode,
    ...telegramIdField(telegramId),
  });
}

/**
 * Get my sent gift links
 */
export async function getMyGiftLinks(telegramId?: number | null) {
  return api.get<unknown[]>(withTelegramId('/gift-links/my', telegramId));
}
